/** Кольцевой буфер реальных кадров камеры. Не влияет на распознавание и не пишет звук. */
import diagnostics from "@/services/diagnostics";

export const REPLAY_FRAME_COUNT = 60;
const MAX_REPLAY_BYTES = 1800 * 1024;
const MAX_WIRE_BYTES = 2 * 1024 * 1024;

export function createRoundRecording(video) {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  let running = false;
  let handle = null;
  let pending = null;
  let frames = [];
  let lastMediaTime = -1;
  let lastFrameId = -1;
  let generation = 0;
  const releaseDiagnostics = diagnostics.registerSource("recorder", () => ({ running, pending: !!pending,
    frames: frames.length, bytes: frames.reduce((sum, frame) => sum + frame.blob.size, 0),
    callbacks: Number(handle !== null), width: canvas.width, height: canvas.height }));
  const usesVideoFrames = typeof video.requestVideoFrameCallback === "function";

  function schedule() {
    if (!running) return;
    handle = usesVideoFrames ? video.requestVideoFrameCallback(capture) : requestAnimationFrame(capture);
  }

  function capture(now, metadata) {
    handle = null;
    if (!running) return;
    schedule();
    // У MediaStream metadata.mediaTime и video.currentTime могут иметь разные начала отсчёта.
    // Для сравнения с точным кадром нарушения используем одну шкалу HTMLVideoElement.
    const mediaTime = video.currentTime;
    const frameId = metadata && Number.isFinite(metadata.presentedFrames) ? metadata.presentedFrames : mediaTime;
    if (pending) diagnostics.metric("recording.skippedPending", 1);
    if (pending || document.hidden || video.readyState < 2 || !video.videoWidth || frameId === lastFrameId) return;
    lastMediaTime = mediaTime;
    lastFrameId = frameId;
    const timestamp = Date.now();
    const token = generation;
    const copyAt = diagnostics.now();
    const scale = Math.min(1, 320 / Math.max(video.videoWidth, video.videoHeight));
    canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
    canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
    try {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      diagnostics.elapsed("recording.copy", copyAt);
      const encodeAt = diagnostics.now();
      pending = new Promise((resolve) => {
        canvas.toBlob((blob) => {
          diagnostics.elapsed("recording.encodeLatency", encodeAt);
          if (blob && token === generation) {
            frames.push({ timestamp, mediaTime, blob });
            if (frames.length > REPLAY_FRAME_COUNT) frames.shift();
          }
          resolve();
        }, "image/jpeg", 0.65);
      }).finally(() => { pending = null; });
    } catch (error) {
      diagnostics.metric("recording.errors", 1);
      // Недоступный кадр не должен мешать игровой детекции.
      pending = null;
    }
  }

  function stop() {
    running = false;
    if (handle !== null) {
      if (usesVideoFrames) video.cancelVideoFrameCallback(handle);
      else cancelAnimationFrame(handle);
    }
    handle = null;
  }

  return {
    start() {
      if (running) return;
      generation += 1;
      frames = [];
      lastMediaTime = -1;
      lastFrameId = -1;
      running = true;
      schedule();
    },
    stop,
    async snapshot(jpeg, timestamp, mediaTime) {
      stop();
      if (pending) await pending;
      const keyFrame = await jpeg;
      const previous = frames.filter((frame) => frame.timestamp < timestamp &&
        (!Number.isFinite(mediaTime) || frame.mediaTime < mediaTime)).slice(-(REPLAY_FRAME_COUNT - 1));
      const recorded = [...previous, { timestamp, blob: keyFrame }];
      if (process.env.VUE_APP_DEV_TOOLS === "true") console.info("[replay] snapshot", {
        buffered: frames.length, previous: previous.length, mediaTime, lastMediaTime,
        firstTimestamp: recorded[0].timestamp, timestamp,
        bytes: recorded.reduce((size, frame) => size + (frame.blob?.size || 0), 0),
      });
      if (recorded.length !== REPLAY_FRAME_COUNT || !keyFrame || timestamp - recorded[0].timestamp > 8000 ||
          recorded.reduce((size, frame) => size + frame.blob.size, 0) > MAX_REPLAY_BYTES) return null;
      return recorded;
    },
    dispose() {
      releaseDiagnostics();
      stop();
      generation += 1;
      frames = [];
      canvas.width = 1;
      canvas.height = 1;
    },
  };
}

/** Старый JPEG первым: ML получает ровно прежнее изображение, запись не меняет вердикт. */
export function buildRecordedViolation(meta, jpeg, recorded) {
  const metadata = {
    type: meta.detectionType === "MOUTH" ? "detected_mouth_open" : "detected_blink",
    detectionType: meta.detectionType,
    gameUUID: meta.gameUUID,
    sessionTime: Math.max(0, Math.round(Number(meta.sessionTime) || 0)),
    timestamp: meta.timestamp,
    score: Math.round((Number(meta.score) || 0) * 1000) / 1000,
    blink: meta.detectionType !== "MOUTH",
    imageSize: jpeg.size,
  };
  if (recorded && recorded.length === REPLAY_FRAME_COUNT) {
    metadata.replay = {
      timestamps: recorded.map((frame) => frame.timestamp),
      frameSizes: recorded.map((frame) => frame.blob.size),
      keyFrameIndex: recorded.length - 1,
      ...(meta.regions ? { regions: meta.regions } : {}),
    };
  }
  const encode = () => new Blob([JSON.stringify(metadata) + "\n", jpeg, ...(metadata.replay ? recorded.map((frame) => frame.blob) : [])],
    { type: "application/octet-stream" });
  const message = encode();
  // Лимит WebSocket относится ко всему сообщению, включая второй JPEG и заголовок.
  // Необязательная запись не должна блокировать проверку исходного нарушения.
  if (message.size > MAX_WIRE_BYTES && metadata.replay) {
    delete metadata.replay;
    return encode();
  }
  return message;
}
